import {
  type FunctionComponent,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import {
  Button,
  Checkbox,
  Input,
  Link,
} from '@dr.pogodin/react-utils';

import { captureException } from '@sentry/react';

import Throbber from 'components/Throbber';
import useUserArtist from 'hooks/useUserArtist';

import { updateArtist } from 'services/artists';

import { getId } from 'utils';

import IconMinus from 'assets/images/icon-circled-minus-24px.svg';

import IconPlus from 'assets/images/icon-circled-plus-24px-smaller.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary-v2.scss';

import smallerInputTheme from 'themes/Input/admin/with-inner-label-46px.scss';

import type { TabRefT } from '../common';

import radioTheme from './radio.scss';

import S from './style.scss';

type PropsT = {
  goToNextTab: () => void;
  goToPrevTab: () => void;
  ref: Ref<TabRefT>;
};

const Tab03: FunctionComponent<PropsT> = ({
  goToNextTab,
  goToPrevTab,
  ref,
}) => {
  const practitioner = useUserArtist();
  const pd = practitioner.data;

  const [
    practiceExperienceYears,
    setPracticeExperienceYears,
  ] = useState(pd?.practiceExperienceYears ?? 0);

  const [
    teachingExperienceYears,
    setTeachingExperienceYears,
  ] = useState(pd?.teachingExperienceYears ?? 0);

  const [
    certificationsAndTraining,
    setCertificationsAndTraining,
  ] = useState<string[]>(() => {
    if (
      pd?.certificationsAndTraining
      && pd.certificationsAndTraining.length > 0
    ) return pd.certificationsAndTraining;

    return [''];
  });

  const [
    specialityAreas,
    setSpecialityAreas,
  ] = useState<string[]>(() => {
    if (
      pd?.specialityAreas
      && pd.specialityAreas.length > 0
    ) return pd.specialityAreas;

    return [''];
  });

  const [
    teachingAndContentCreationConsent,
    setTeachingAndContentCreationConsent,
  ] = useState(pd?.teachingAndContentCreationConsent);

  useEffect(() => {
    // TODO: Refactor.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPracticeExperienceYears(pd?.practiceExperienceYears ?? 0);
    setTeachingExperienceYears(pd?.teachingExperienceYears ?? 0);

    setCertificationsAndTraining(
      pd?.certificationsAndTraining?.length
        ? pd.certificationsAndTraining : [''],
    );

    setSpecialityAreas(
      pd?.specialityAreas?.length
        ? pd.specialityAreas : [''],
    );

    setTeachingAndContentCreationConsent(pd?.teachingAndContentCreationConsent);
  }, [pd]);

  const certsAndTrainingNodes: ReactNode[] = [];
  for (let i = 0; i < certificationsAndTraining.length; ++i) {
    const item = certificationsAndTraining[i];

    certsAndTrainingNodes.push(
      <div key={`certifications-and-training-${i}`} styleName="inputRow">
        <Input
          onChange={(e) => {
            const next = [...certificationsAndTraining];
            next[i] = e.target.value;
            setCertificationsAndTraining(next);
          }}
          theme={smallerInputTheme}
          value={item}
        />
        {certificationsAndTraining.length > 1 ? (
          <Link
            keepScrollPosition
            onClick={() => {
              const next = [...certificationsAndTraining];
              next.splice(i, 1);
              setCertificationsAndTraining(next);
            }}
            styleName="iconButtonLink"
            to=""
          >
            <IconMinus />
          </Link>
        ) : <div styleName="plusMinusPlaceholder" />}
        {i === certificationsAndTraining.length - 1 ? (
          <Link
            keepScrollPosition
            onClick={() => {
              setCertificationsAndTraining([
                ...certificationsAndTraining,
                '',
              ]);
            }}
            styleName="iconButtonLink"
            to=""
          >
            <IconPlus />
          </Link>
        ) : <div styleName="plusMinusPlaceholder" />}
      </div>,
    );
  }

  const specialityAreasNodes: ReactNode[] = [];
  for (let i = 0; i < specialityAreas.length; ++i) {
    const item = specialityAreas[i];

    specialityAreasNodes.push(
      <div key={`speciality-areas-${i}`} styleName="inputRow">
        <Input
          onChange={(e) => {
            const next = [...specialityAreas];
            next[i] = e.target.value;
            setSpecialityAreas(next);
          }}
          theme={smallerInputTheme}
          value={item}
        />
        {specialityAreas.length > 1 ? (
          <Link
            keepScrollPosition
            onClick={() => {
              const next = [...specialityAreas];
              next.splice(i, 1);
              setSpecialityAreas(next);
            }}
            styleName="iconButtonLink"
            to=""
          >
            <IconMinus />
          </Link>
        ) : <div styleName="plusMinusPlaceholder" />}
        {i === specialityAreas.length - 1 ? (
          <Link
            keepScrollPosition
            onClick={() => {
              setSpecialityAreas([
                ...specialityAreas,
                '',
              ]);
            }}
            styleName="iconButtonLink"
            to=""
          >
            <IconPlus />
          </Link>
        ) : <div styleName="plusMinusPlaceholder" />}
      </div>,
    );
  }

  const [busy, setBusy] = useState(false);

  const save = useCallback(async () => {
    try {
      setBusy(true);

      const filteredCertificationsAndTraning = certificationsAndTraining
        .map((item) => item.trim())
        .filter((item) => !!item);

      const filteredSpecialityAreas = specialityAreas
        .map((item) => item.trim())
        .filter((item) => !!item);

      const a = await updateArtist(getId(practitioner.data), {
        certificationsAndTraining: filteredCertificationsAndTraning,
        practiceExperienceYears,
        specialityAreas: filteredSpecialityAreas,
        teachingAndContentCreationConsent,
        teachingExperienceYears,
      });

      practitioner.set(a);
    } catch (error) {
      captureException(error);
      // eslint-disable-next-line no-alert
      alert('Failed to save data');
    }

    setBusy(false);
  }, [
    certificationsAndTraining,
    practiceExperienceYears,
    practitioner,
    specialityAreas,
    teachingAndContentCreationConsent,
    teachingExperienceYears,
  ]);

  useImperativeHandle(ref, () => ({
    save,
    valid: true,
  }), [save]);

  return (
    <div className={S.container}>
      <p>
        Tell us about your path in wellness. From formal training to personal
        practice, we want to understand what has shaped your approach to healing
        and teaching.
      </p>
      <div styleName="row">
        <Input
          label="Years of Practice Experience"
          onBlur={() => {
            if (practiceExperienceYears < 0) {
              setPracticeExperienceYears(0);
            } else if (practiceExperienceYears > 100) {
              setPracticeExperienceYears(100);
            }
          }}
          onChange={(e) => {
            setPracticeExperienceYears(parseInt(e.target.value));
          }}
          type="number"
          value={practiceExperienceYears.toString()}
        />
        <Input
          label="Years of Teaching Experience"
          onBlur={() => {
            if (teachingExperienceYears < 0) {
              setTeachingExperienceYears(0);
            } else if (teachingExperienceYears > 100) {
              setTeachingExperienceYears(100);
            }
          }}
          onChange={(e) => {
            setTeachingExperienceYears(parseInt(e.target.value));
          }}
          type="number"
          value={teachingExperienceYears.toString()}
        />
      </div>
      <h2>Certifications & Training</h2>
      {certsAndTrainingNodes}
      <h2>Speciality Areas</h2>
      {specialityAreasNodes}
      <h2>Teaching and Content Creation</h2>
      <p styleName="p">
        Soul Sanctuary focuses on 2-8 minute segments. Are you comfortable with
        creating content within these constraints?
      </p>
      <div styleName="checkboxes">
        <Checkbox
          checked={teachingAndContentCreationConsent === true}
          label="Yes"
          onChange={() => {
            setTeachingAndContentCreationConsent(true);
          }}
          theme={radioTheme}
        />
        <Checkbox
          checked={teachingAndContentCreationConsent === false}
          label="No"
          onChange={() => {
            setTeachingAndContentCreationConsent(false);
          }}
          theme={radioTheme}
        />
      </div>
      <div styleName="hr" />
      <div styleName="buttons">
        <Button
          onClick={() => {
            void (async () => {
              await save();
              goToPrevTab();
            })();
          }}
          theme={secondaryButtonTheme}
        >
          Previous
        </Button>
        <Button
          onClick={() => {
            void (async () => {
              await save();
              goToNextTab();
            })();
          }}
        >
          Next
        </Button>
      </div>
      {busy ? <Throbber withOverlay /> : null}
    </div>
  );
};

export default Tab03;
